import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { DataSnapshot, Skill } from "../lib/types";
import { useRoute } from "../router";
import { BrandRail } from "../components/BrandRail";
import { ScoreRadar } from "../components/ScoreRadar";
import { getSkillBody, findSkill } from "../lib/api";
import { scoreClass, tierClass, statusClass, TIER_LABEL, STATUS_LABEL } from "../lib/scoring";
import { formatDate } from "../lib/format";
import { listPlugins } from "../lib/plugins";

const STRUCTURAL_DIMS: Array<[keyof Skill["structuralScores"], string]> = [
  ["discovery", "Discovery"],
  ["clarity", "Clarity"],
  ["completeness", "Completeness"],
  ["structure", "Structure"],
  ["effectiveness", "Effectiveness"],
];

const LLM_DIMS: Array<[keyof NonNullable<Skill["llmScores"]>, string]> = [
  ["clarity", "Clarity"],
  ["actionability", "Actionability"],
  ["completeness", "Completeness"],
];

const ISSUE_ORDER = ["broken", "missing", "long", "short", "low"] as const;

interface Props {
  data: DataSnapshot;
  skillName: string;
}

export function SkillDetail({ data, skillName }: Props) {
  const [, navigate] = useRoute();
  const [showSource, setShowSource] = useState(false);
  const [copied, setCopied] = useState(false);

  const skill = findSkill(data.skills, skillName);

  useEffect(() => {
    setShowSource(false);
  }, [skillName]);

  const { data: bodyContent } = useQuery({
    queryKey: ["skill-body", skillName],
    queryFn: () => getSkillBody(skillName),
    enabled: showSource,
  });

  useEffect(() => {
    document.title = `${skillName} · skill-grinder`;
  }, [skillName]);

  const groupedIssues = useMemo(() => {
    if (!skill) return [];
    const groups = new Map<string, typeof skill.issues>();
    for (const kind of ISSUE_ORDER) groups.set(kind, []);
    for (const i of skill.issues) {
      const list = groups.get(i.kind) ?? [];
      list.push(i);
      groups.set(i.kind, list);
    }
    return ISSUE_ORDER.filter((k) => (groups.get(k)?.length ?? 0) > 0).map((k) => ({
      kind: k,
      items: groups.get(k)!,
    }));
  }, [skill]);

  async function copyLink() {
    const url = `${window.location.origin}${window.location.pathname}#/skill/${encodeURIComponent(skillName)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  if (!skill) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div>
          <h1 className="font-display text-t-40 font-extrabold leading-none">Skill not found</h1>
          <p className="mt-4 font-mono text-t-12 uppercase tracking-widest text-muted">
            // &quot;{skillName}&quot; does not exist
          </p>
          <button
            type="button"
            onClick={() => navigate({ name: "monitor" })}
            className="mt-6 block font-mono text-t-12 uppercase tracking-widest underline underline-offset-4 hover:text-accent"
          >
            ← Back to skills
          </button>
        </div>
      </div>
    );
  }

  const sc = scoreClass(skill.combinedPercentage);
  const tierCl = tierClass(skill.tier);
  const statusCl = statusClass(skill.status);

  return (
    <div className="grid min-h-screen skill-detail-grid" style={{ gridTemplateColumns: "220px 1fr" }}>
      <BrandRail
        counts={{
          monitor: data.stats.total,
          issues: data.stats.statusCounts.warning + data.stats.statusCounts.critical,
        }}
      />

      <main className="overflow-y-auto">
        <div className="max-w-4xl mx-auto px-8 py-10">
          {/* Back + meta */}
          <div className="flex items-baseline justify-between mb-10">
            <button
              type="button"
              onClick={() => navigate({ name: "monitor" })}
              className="font-mono text-t-11 uppercase tracking-widest text-muted hover:text-accent hover-shift"
            >
              ← Back
            </button>
            <div className="font-mono text-t-10 uppercase tracking-[0.14em] text-muted font-medium">
              // {skill.category} · {skill.lineCount} lines · {formatDate(skill.lastScored)}
            </div>
          </div>

          {/* Hero */}
          <div className="border-b border-fg pb-8 mb-8">
            <h1 className="font-display font-extrabold text-t-48 leading-[0.95] tracking-[-0.03em] mb-3">
              {skill.name}
            </h1>
            <div className="flex gap-8 items-start flex-wrap">
              <div>
                <div className={`font-display font-extrabold text-t-120 leading-[0.9] tracking-[-0.05em] tabular-nums text-${sc}`}>
                  {skill.combinedPercentage}
                  <span className="font-mono text-t-36 font-normal text-muted ml-1">%</span>
                </div>
              </div>
              <ScoreRadar skill={skill} />
            </div>
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 font-mono text-t-10 uppercase tracking-[0.14em] text-muted font-medium">
              <span className={`text-${tierCl}`}>// {TIER_LABEL[skill.tier]}</span>
              <span className={`text-${statusCl}`}>// {STATUS_LABEL[skill.status]}</span>
              {skill.delta != null && (
                <span>// {skill.delta > 0 ? "+" : ""}{skill.delta} since last snapshot</span>
              )}
            </div>
          </div>

          {/* Two-column: structural + LLM */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 mb-10">
            {/* Structural */}
            <div>
              <h2 className="font-mono text-t-10 uppercase tracking-[0.14em] text-muted font-medium mb-4">
                // Structural · {skill.structuralTotal}/50
              </h2>
              <div className="space-y-1">
                {STRUCTURAL_DIMS.map(([key, label]) => {
                  const val = skill.structuralScores[key];
                  const pct = Math.round((val / 10) * 100);
                  const cls = val >= 8 ? "good" : val >= 5 ? "warn" : "bad";
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <span className="font-mono text-t-11 w-28 uppercase tracking-wide text-fg">{label}</span>
                      <div className="flex-1 h-2 border border-fg bg-bg">
                        <div
                          className={`h-full bg-${cls}`}
                          style={{ width: `${pct}%`, transition: "width 0.4s ease" }}
                        />
                      </div>
                      <span className={`font-mono text-t-11 tabular-nums w-8 text-right text-${cls}`}>
                        {val}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* LLM */}
            {skill.llmScores && (
              <div>
                <h2 className="font-mono text-t-10 uppercase tracking-[0.14em] text-muted font-medium mb-4">
                  // LLM · {skill.llmTotal ?? 0}/30{skill.llmProvider ? ` · ${skill.llmProvider}` : ""}
                </h2>
                <div className="space-y-1">
                  {LLM_DIMS.map(([key, label]) => {
                    const val = skill.llmScores![key];
                    const pct = Math.round((val / 10) * 100);
                    const cls = val >= 8 ? "good" : val >= 5 ? "warn" : "bad";
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <span className="font-mono text-t-11 w-28 uppercase tracking-wide text-fg">{label}</span>
                        <div className="flex-1 h-2 border border-fg bg-bg">
                          <div
                            className={`h-full bg-${cls}`}
                            style={{ width: `${pct}%`, transition: "width 0.4s ease" }}
                          />
                        </div>
                        <span className={`font-mono text-t-11 tabular-nums w-8 text-right text-${cls}`}>
                          {val}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Issues */}
          {skill.issues.length > 0 && (
            <div className="mb-10">
              <h2 className="font-mono text-t-10 uppercase tracking-[0.14em] text-muted font-medium mb-4">
                // Issues · {skill.issues.length}
              </h2>
              <div className="space-y-0">
                {groupedIssues.map((g) => (
                  <div key={g.kind} className="py-4 border-t border-rule">
                    <div className="font-mono text-t-10 uppercase tracking-[0.1em] text-muted mb-2">
                      {g.kind} ({g.items.length})
                    </div>
                    {g.items.map((i, idx) => (
                      <div key={idx} className="py-2 border-b border-rule/50 text-t-13 leading-[1.5]">
                        <div className="flex gap-2">
                          <span className="font-mono text-bad font-bold shrink-0">!</span>
                          <span>{i.text}</span>
                        </div>
                        {i.fix && (
                          <div className="mt-1 ml-5 font-mono text-t-10 text-muted">→ {i.fix}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {skill.issues.length === 0 && (
            <div className="mb-10 font-mono text-t-11 text-good font-medium">
              <span className="text-good font-bold">✓</span> No issues · ready to ship
            </div>
          )}

          {/* Plugins */}
          {listPlugins()
            .filter((p) => p.DetailSection != null)
            .map((plugin) => {
              const Sec = plugin.DetailSection!;
              return (
                <div key={plugin.id} className="mb-10">
                  <h2 className="font-mono text-t-10 uppercase tracking-[0.14em] text-muted font-medium mb-4">
                    // {plugin.name}
                  </h2>
                  <Sec skill={skill} />
                </div>
              );
            })}

          {/* SKILL.md source */}
          <div className="mb-10">
            <button
              type="button"
              onClick={() => setShowSource(!showSource)}
              className="font-mono text-t-10 uppercase tracking-[0.14em] text-muted hover:text-accent hover-shift mb-4"
            >
              {showSource ? "// Hide SKILL.md source" : "// View SKILL.md source"}
            </button>
            {showSource && bodyContent && (
              <div className="border border-fg p-6 max-h-[600px] overflow-y-auto font-mono text-t-12 leading-relaxed prose prose-sm prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{bodyContent}</ReactMarkdown>
              </div>
            )}
            {showSource && !bodyContent && (
              <div className="border border-fg p-6 font-mono text-t-12 text-muted">// loading...</div>
            )}
          </div>

          {/* Actions footer */}
          <div className="border-t border-fg pt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void copyLink()}
              className="action-btn"
            >
              {copied ? "✓ Link copied" : "⎘ Copy link"}
            </button>
            <a
              href={`#/?skill=${encodeURIComponent(skillName)}`}
              className="action-btn"
            >
              → Monitor
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
